import axios from "axios";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

// Fetch an authenticated file (PDF etc.) and hand it to the browser as a download
export async function downloadFile(path, filename) {
  const { data } = await api.get(path, { responseType: "blob" });
  const url = URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

// Fetch an authenticated file and return an object URL for inline preview (iframe/embed).
// Caller is responsible for URL.revokeObjectURL when done.
export async function previewFile(path) {
  const { data } = await api.get(path, { responseType: "blob" });
  return URL.createObjectURL(data);
}

// Short-lived, read-only token for <img>/<a> asset URLs so the full session JWT never rides in a URL.
const ASSET_KEY = "sw_asset_token";
let assetTimer = null;
export async function ensureAssetToken() {
  if (!localStorage.getItem("sw_token")) return "";
  try {
    const { data } = await api.get("/asset-token");
    if (data?.token) localStorage.setItem(ASSET_KEY, data.token);
    if (!assetTimer) assetTimer = setInterval(() => ensureAssetToken(), 20 * 60 * 1000);
  } catch {
    /* keep the previous token */
  }
  return localStorage.getItem(ASSET_KEY) || "";
}
export const getAssetToken = () => localStorage.getItem(ASSET_KEY) || "";

// Staff photo URL with a short-lived asset token, from a storage path or a URL saved earlier
export const photoHref = (u) => {
  if (!u) return "";
  const base = String(u).split("?")[0];
  const i = base.indexOf("/photos/");
  const path = i >= 0 ? base.slice(i + 8) : base;
  const t = getAssetToken();
  if (!t) ensureAssetToken();
  return `${API}/photos/${path}?t=${t}`;
};

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("sw_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// On a 401, refresh the access token once (using the httpOnly refresh cookie)
// and replay the original request. One shared in-flight refresh handles bursts.
let refreshing = null;
api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;
    const url = original?.url || "";
    if (
      status === 401 &&
      original &&
      !original._retry &&
      !url.includes("/auth/refresh") &&
      !url.includes("/auth/login")
    ) {
      original._retry = true;
      const hadToken = !!localStorage.getItem("sw_token");
      try {
        refreshing =
          refreshing ||
          api
            .post("/auth/refresh", {}, { withCredentials: true })
            .then((r) => r.data.access_token);
        const token = await refreshing;
        refreshing = null;
        if (token) {
          localStorage.setItem("sw_token", token);
          ensureAssetToken();
          original.headers = original.headers || {};
          original.headers.Authorization = `Bearer ${token}`;
          return api(original);
        }
      } catch (e) {
        refreshing = null;
        localStorage.removeItem("sw_token");
        if (
          hadToken &&
          typeof window !== "undefined" &&
          !window.location.pathname.endsWith("/login")
        ) {
          window.location.href = "/admin/login";
        }
      }
    }
    return Promise.reject(error);
  },
);

// Posts to a streaming AI endpoint and calls onDelta with each token.
export async function streamAi(path, body, onDelta) {
  const res = await fetch(`${API}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("sw_token")}`,
    },
    body: JSON.stringify(body),
  });
  if (!res.ok || !res.body) {
    let detail = "";
    try {
      detail = formatApiErrorDetail((await res.json()).detail);
    } catch {
      detail = "";
    }
    throw new Error(detail || `The assistant could not answer (${res.status})`);
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let last = {};
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const blocks = buffer.split("\n\n");
    buffer = blocks.pop() ?? "";
    for (const block of blocks) {
      const line = block.split("\n").find((l) => l.startsWith("data: "));
      if (!line) continue;
      let event;
      try {
        event = JSON.parse(line.slice(6));
      } catch {
        continue;
      }
      if (event.error) throw new Error(event.error);
      if (event.delta) onDelta(event.delta);
      if (event.done) last = event;
    }
  }
  return last;
}

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Warm the asset token on load so <img>/<a> URLs have one ready for the first paint.
if (typeof window !== "undefined" && localStorage.getItem("sw_token")) {
  ensureAssetToken();
}
